import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'

// Query Keys
const FEEDBACK_KEY = 'feedback'

// Types
export type FeedbackCategory = 'problem' | 'suggestion' | 'improvement'
export type FeedbackStatus = 'new' | 'reviewed' | 'in_progress' | 'resolved' | 'rejected'

export interface Feedback {
  id: string
  tenant_id: string
  employee_id: string
  category: FeedbackCategory
  title: string
  description: string
  proposed_solution?: string | null
  status: FeedbackStatus
  admin_comment?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  created_at: string
  updated_at: string
  employees?: {
    id: string
    first_name: string
    last_name: string
    middle_name?: string | null
    departments_employees_department_idTodepartments?: { id: string; name: string } | null
    positions?: { id: string; name: string } | null
  }
}

export interface FeedbackFilters {
  search?: string
  category?: string
  status?: string
  employee_id?: string
  date_from?: string
  date_to?: string
  page?: number
  limit?: number
  sort?: string
}

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  problem: 'Проблема',
  suggestion: 'Предложение',
  improvement: 'Улучшение',
}

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'Новый',
  reviewed: 'Рассмотрен',
  in_progress: 'В работе',
  resolved: 'Решён',
  rejected: 'Отклонён',
}

// Hooks
export const useFeedbackList = (filters: FeedbackFilters = {}) => {
  return useQuery({
    queryKey: [FEEDBACK_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getFeedback(filters)
      return {
        data: (response.data || []) as Feedback[],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
      }
    },
    staleTime: 30 * 1000,
  })
}

export const useFeedbackDetail = (id: string) => {
  return useQuery({
    queryKey: [FEEDBACK_KEY, id],
    queryFn: async () => {
      const response = await apiClient.getFeedbackById(id)
      return response as Feedback
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useUpdateFeedbackStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { status: string; admin_comment?: string }
    }) => {
      return apiClient.updateFeedbackStatus(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEEDBACK_KEY] })
    },
  })
}
